import content from '../../../data/thai-combat-content.json';
import type { EnemyId } from './enemies';

export type WeaponSchool = 'muay' | 'bow' | 'sword' | 'spear';
export type RuntimeGhostId = EnemyId;
export type GhostContentId = keyof typeof content.ghosts;
export function ghostContentId(id: RuntimeGhostId): GhostContentId {
  return id === 'violent-dead' ? 'tai-hong' : id;
}
export type DropItemType = 'ghost-part' | 'weapon' | 'armor';

export type ItemDrop = {
  itemId: string;
  nameTh: string;
  type: DropItemType;
  quantity: number;
};

export type DamageAffinityResult = {
  damage: number;
  multiplier: number;
  blocked: boolean;
};

export const THAI_COMBAT_CONTENT = content;

export const STARTER_WEAPON_SCHOOLS = content.weaponSchools.map((school) => ({
  ...school,
  id: school.id as WeaponSchool,
}));

export function isWeaponSchool(value: unknown): value is WeaponSchool {
  return value === 'muay' || value === 'bow' || value === 'sword' || value === 'spear';
}

export function normalizeWeaponSchool(value: unknown, fallback: WeaponSchool = 'sword'): WeaponSchool {
  return isWeaponSchool(value) ? value : fallback;
}

export function weaponSchoolNameTh(school: WeaponSchool): string {
  return STARTER_WEAPON_SCHOOLS.find((entry) => entry.id === school)?.nameTh ?? school;
}

export function ghostAffinity(enemyId: RuntimeGhostId, school: WeaponSchool): number {
  const ghost = content.ghosts[ghostContentId(enemyId)];
  return ghost.affinity[school];
}

export function applyGhostAffinity(
  baseDamage: number,
  enemyId: RuntimeGhostId,
  school: WeaponSchool,
): DamageAffinityResult {
  const multiplier = ghostAffinity(enemyId, school);
  if (multiplier <= 0) return { damage: 0, multiplier: 0, blocked: true };
  return {
    damage: Math.max(1, Math.round(Math.max(0, baseDamage) * multiplier)),
    multiplier,
    blocked: false,
  };
}

export function rollGhostDrops(
  enemyId: RuntimeGhostId,
  rng: () => number = Math.random,
): ItemDrop[] {
  const rules = content.ghosts[ghostContentId(enemyId)].drops;
  const drops: ItemDrop[] = [];
  for (const rule of rules) {
    const roll = Math.max(0, Math.min(0.999999999, rng()));
    if (roll >= rule.chance) continue;
    drops.push({
      itemId: rule.itemId,
      nameTh: rule.nameTh,
      type: rule.type as DropItemType,
      quantity: Math.max(1, Math.trunc(rule.quantity)),
    });
  }
  return drops;
}

export function itemLabel(itemId: string): string {
  for (const ghost of Object.values(content.ghosts)) {
    const found = ghost.drops.find((drop) => drop.itemId === itemId);
    if (found) return found.nameTh;
  }
  return itemId;
}
